import { Button as KendoButton } from '@progress/kendo-react-buttons';
import { Chart, ChartCategoryAxis, ChartCategoryAxisItem, ChartLegend, ChartSeries, ChartSeriesItem, ChartTitle, ChartValueAxis, ChartValueAxisItem } from '@progress/kendo-react-charts';
import { Grid, GridCustomCellProps, GridColumn as Column, GridNoRecords, GridToolbar } from '@progress/kendo-react-grid';
import { formatNumber } from '@progress/kendo-intl';
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import DateTimeCell from 'components/DateTimeCell';
import { fetchApi } from 'core/fetchApi';
import { parseJSON } from 'date-fns';
import { format } from 'date-fns-tz';
import { useMemo, useState } from 'react';
import { useLoaderData, useRevalidator } from 'react-router-dom';

type ViewModel = {
  CurrentScore: number;
  PercentileRank: number | null;
  ThirtyDayScoreChange: number;
  PickupOnTimePercent: number | null;
  DeliveryOnTimePercent: number | null;
  RecentEvents: ScoreEventViewModel[];
  WeightLegend: WeightLegendViewModel[];
}

type ScoreEventViewModel = {
  CarrierScoreEventID: number;
  CreatedOn: string;
  EventType: number;
  EventTypeLabel: string;
  Description: string;
  ScoreChange: number;
  ScoreAfter: number;
  IsUndone: boolean;
  CanDispute: boolean;
  DisputeStatus: number | null;
  DisputeStatusLabel: string;
  DisputeSubmittedOn: string | null;
  DisputeResolvedOn: string | null;
  DisputeNote: string | null;
  DisputeResolutionNote: string | null;
}

type WeightLegendViewModel = {
  Label: string;
  Weight: number;
  ScoreChange: number;
}

type SubmitDisputeResponse = {
  Success: boolean;
  ErrorMessage: string;
}

interface DisputeCommandCellProps extends GridCustomCellProps {
  openDispute: (item: ScoreEventViewModel) => void;
}

const DisputeCommandCell = ({ dataItem, openDispute, tdProps }: DisputeCommandCellProps) => {
  const hasDispute = dataItem.DisputeStatus !== null && dataItem.DisputeStatus !== undefined;
  const showDisputeButton = dataItem.CanDispute && !hasDispute && !dataItem.IsUndone;

  return <td {...tdProps} className={[tdProps?.className, "k-command-cell"].filter(Boolean).join(" ")}>
    {showDisputeButton && (
      <KendoButton themeColor="warning" onClick={() => openDispute(dataItem)}>
        Dispute
      </KendoButton>
    )}
  </td>;
};

const ScoreChangeCell = (props: GridCustomCellProps) => {
  if (props.rowType !== 'data') return <td {...props.tdProps} />;

  const value = Number(props.dataItem.ScoreChange || 0);
  const color = value > 0 ? '#2ecc71' : value < 0 ? '#f44336' : undefined;
  const prefix = value > 0 ? '+' : '';

  return <td {...props.tdProps} style={{ ...props.tdProps?.style, color, fontWeight: 600 }}>{prefix}{formatNumber(value, 'n2')}</td>;
};

const ScoreAfterCell = (props: GridCustomCellProps) => {
  if (props.rowType !== 'data') return <td {...props.tdProps} />;

  return <td {...props.tdProps}>{formatNumber(Number(props.dataItem.ScoreAfter || 0), 'n2')}</td>;
};

const DisputeStatusCell = (props: GridCustomCellProps) => {
  if (props.rowType !== 'data') return <td {...props.tdProps} />;

  if (props.dataItem.IsUndone) {
    return <td {...props.tdProps}>Undone</td>;
  }

  const hasDispute = props.dataItem.DisputeStatus !== null && props.dataItem.DisputeStatus !== undefined;
  if (!hasDispute) {
    return <td {...props.tdProps}>Not Disputed</td>;
  }

  const submittedOn = props.dataItem.DisputeSubmittedOn ? format(parseJSON(props.dataItem.DisputeSubmittedOn), 'LL/dd/yyyy') : '';
  const resolvedOn = props.dataItem.DisputeResolvedOn ? format(parseJSON(props.dataItem.DisputeResolvedOn), 'LL/dd/yyyy') : '';
  const label = props.dataItem.DisputeStatusLabel || 'Submitted';

  return <td {...props.tdProps}>
    <div>{label}</div>
    {submittedOn && <div style={{ fontSize: 12, opacity: 0.7 }}>Submitted {submittedOn}</div>}
    {resolvedOn && <div style={{ fontSize: 12, opacity: 0.7 }}>Resolved {resolvedOn}</div>}
  </td>;
};

const formatPercentile = (value: number | null | undefined) => {
  if (value === null || value === undefined) {
    return "—";
  }
  return `${value.toFixed(1)}%`;
};

const formatPercent = (value: number | null | undefined) => {
  if (value === null || value === undefined) {
    return "—";
  }

  return formatNumber(value, 'p1');
}

const formatScoreDelta = (value: number | null | undefined) => {
  if (value === null || value === undefined) {
    return "—";
  }

  const rounded = Math.round(value);
  if (rounded > 0) {
    return `+${rounded.toLocaleString()}`;
  }

  if (rounded < 0) {
    return rounded.toLocaleString();
  }

  return "0";
};

const getPercentileProgress = (percentile: number | null | undefined) => {
  if (percentile === null || percentile === undefined) {
    return 0;
  }

  return Math.max(0, Math.min(1, percentile / 100));
};

const Scorecard = () => {
  const data = useLoaderData() as ViewModel;
  const revalidator = useRevalidator();
  const [legendOpen, setLegendOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<ScoreEventViewModel | null>(null);
  const [disputeNote, setDisputeNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState('');

  const orderedEvents = useMemo(
    () => [...data.RecentEvents].sort((a, b) => parseJSON(a.CreatedOn).getTime() - parseJSON(b.CreatedOn).getTime()),
    [data.RecentEvents],
  );

  const chartPoints = orderedEvents.map((event) => ({ date: parseJSON(event.CreatedOn), score: Number(event.ScoreAfter) }));
  const chartCategories = chartPoints.map((point) => point.date);
  const chartScores = chartPoints.map((point) => point.score);

  const submitDispute = () => {
    if (!selectedEvent) return;

    const note = disputeNote.trim();
    if (!note) {
      setSnackbar('A dispute note is required.');
      return;
    }

    setSubmitting(true);
    fetchApi<SubmitDisputeResponse>({
      url: 'SubmitScoreEventDispute',
      method: 'POST',
      payload: {
        CarrierScoreEventID: selectedEvent.CarrierScoreEventID,
        Note: note,
      },
    })
      .then((response) => {
        if (!response.Success) {
          setSnackbar(response.ErrorMessage || 'Unable to submit dispute.');
          return;
        }

        setSelectedEvent(null);
        setDisputeNote('');
        setSnackbar('Dispute submitted.');
        revalidator.revalidate();
      })
      .catch(() => {
        setSnackbar('Error submitting dispute.');
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const MyDisputeCommandCell = (props: GridCustomCellProps) => (
    <DisputeCommandCell
      {...props}
      openDispute={(event) => {
        setSelectedEvent(event);
        setDisputeNote(event.DisputeNote || '');
      }}
    />
  );

  return <>
    <Box
      sx={{ display: "grid", gap: 2, mb: 3, gridTemplateColumns: {
        xs: '1fr',
        sm: 'repeat(2, minmax(0, 1fr))',
        md: 'repeat(3, minmax(0, 1fr))',
        lg: 'repeat(5, minmax(0, 1fr))',
      } }}
    >
      <Card>
        <CardContent>
          <Typography variant="overline">Current Score</Typography>
          <Typography variant="h3">{formatNumber(Number(data.CurrentScore), 'n0')}</Typography>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <Typography variant="overline">30-Day Change</Typography>
          <Typography variant="h3">{formatScoreDelta(data.ThirtyDayScoreChange)}</Typography>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <Typography variant="overline">Pickup On Time</Typography>
          <Typography variant="h3">{formatPercent(data.PickupOnTimePercent)}</Typography>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <Typography variant="overline">Delivery On Time</Typography>
          <Typography variant="h3">{formatPercent(data.DeliveryOnTimePercent)}</Typography>
        </CardContent>
      </Card>
      <Card sx={{ position: 'relative', overflow: 'hidden' }}>
        <CardContent>
          <Typography variant="overline">Percentile Rank</Typography>
          <Typography variant="h3">{formatPercentile(data.PercentileRank)}</Typography>
        </CardContent>
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            bottom: 0,
            height: 4,
            width: `${getPercentileProgress(data.PercentileRank) * 100}%`,
            backgroundColor: 'primary.main',
          }}
        />
      </Card>
    </Box>

    {!data.RecentEvents.length && (
      <Alert severity="info" sx={{ mb: 2 }}>
        No score events available yet.
      </Alert>
    )}

    {!!chartPoints.length && (
      <Box sx={{ mb: 3 }}>
        <Chart transitions={false}>
          <ChartTitle position="top" text="Score Trend" />
          <ChartLegend visible={false} />
          <ChartCategoryAxis>
            <ChartCategoryAxisItem categories={chartCategories} type="date" baseUnit="days" labels={{ format: 'M/d' }} />
          </ChartCategoryAxis>
          <ChartValueAxis>
            <ChartValueAxisItem />
          </ChartValueAxis>
          <ChartSeries>
            <ChartSeriesItem type="line" data={chartScores} color="#4dabf5" markers={{ visible: true }} />
          </ChartSeries>
        </Chart>
      </Box>
    )}

    <Grid scrollable="none" data={data.RecentEvents} size="small">
      <GridToolbar>
        <Button variant="outlined" onClick={() => setLegendOpen(true)}>Legend</Button>
        <Button variant="outlined" component="a" href="https://carrier.load1.com/docs/carrier_scorecard_guide.html" target="_blank" rel="noopener noreferrer">Scorecard Guide</Button>
      </GridToolbar>
      <GridNoRecords>
        No score events found.
      </GridNoRecords>
      <Column field="CreatedOn" title="Recorded" cells={{ data: DateTimeCell }} />
      <Column field="EventTypeLabel" title="Event" />
      <Column field="Description" title="Description" />
      <Column field="ScoreChange" title="Change" cells={{ data: ScoreChangeCell }} />
      <Column field="ScoreAfter" title="Score" cells={{ data: ScoreAfterCell }} />
      <Column title="Status" cells={{ data: DisputeStatusCell }} />
      <Column title="Dispute" cells={{ data: MyDisputeCommandCell }} />
    </Grid>

    <Dialog open={legendOpen} onClose={() => setLegendOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Score Event Legend</DialogTitle>
      <DialogContent>
        <p>The following score event weights are used to calculate your carrier score. Scores are 2x for the first 60 events.</p>
        {!data.WeightLegend.length && <Typography>No legend data available.</Typography>}
        {!!data.WeightLegend.length && <List dense>
          {data.WeightLegend.map((item, index) => (
            <ListItem key={`${item.Label}-${index}`} disableGutters>
              <ListItemText
                primary={<>
                  <Box component="span" sx={{ color: item.ScoreChange > 0 ? '#2ecc71' : item.ScoreChange < 0 ? '#f44336' : undefined, fontWeight: 600 }}>
                    {item.ScoreChange > 0 ? '+' : ''}{formatNumber(item.ScoreChange, 'n2')}
                  </Box>
                  {`: ${item.Label}`}
                </>}
              />
            </ListItem>
          ))}
        </List>}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setLegendOpen(false)}>Close</Button>
      </DialogActions>
    </Dialog>

    <Dialog
      open={!!selectedEvent}
      onClose={() => !submitting && setSelectedEvent(null)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Dispute Score Event</DialogTitle>
      <DialogContent>
        {selectedEvent && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {selectedEvent.EventTypeLabel} on {format(parseJSON(selectedEvent.CreatedOn), 'LL/dd/yyyy')}
            </Typography>
            <TextField
              autoFocus
              margin="dense"
              label="Note"
              value={disputeNote}
              onChange={(event) => setDisputeNote(event.target.value)}
              fullWidth
              multiline
              minRows={4}
              disabled={submitting}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setSelectedEvent(null)} disabled={submitting}>Cancel</Button>
        <Button onClick={submitDispute} disabled={submitting} variant="contained">Submit Dispute</Button>
      </DialogActions>
    </Dialog>

    <Snackbar
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      open={!!snackbar}
      autoHideDuration={6000}
      onClose={(_, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar('');
      }}
      message={snackbar}
      action={
        <IconButton size="small" aria-label="close" color="inherit" onClick={() => setSnackbar('')}>
          <CloseIcon fontSize="small" />
        </IconButton>
      }
    />
  </>;
}

export default Scorecard;
